import { addMinutes, differenceInMinutes, format, isPast } from "date-fns";
import { FC, useCallback, useMemo } from "react";

import { AttendanceReportButton } from "./AttendanceReportButton";

import { ExtendedClassSessionSummaryType } from "api/api-data-types";
import { serverGenerateRecordingUrl } from "api/api-server-actions";
import {
  ClassSessionCategory,
  RecordingSessionState,
  RecordingSessionType,
} from "components/server-types";
import { t } from "i18n/i18n";
import { Menu, MenuItem } from "mds/components/Menu";
import {
  SortableBody,
  SortableHeader,
  SortableTable,
  SortableTableExportButton,
  SortableTableWrapper,
} from "mds/components/SortableTable";
import { LinkButton } from "mds/components/buttons/LinkButton";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { DotMenuVerticalIcon, EyeNotVisibleIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentSectionTitle,
  selectCurrentUserIsMPAdmin,
  selectExtendedClassSessionSummariesForDateRange,
  selectRecordingSessionsByClassSessionId,
} from "store/selectors";
import { toastErrorMessage } from "utils/alerts";
import {
  getClassSessionDurationInMinutes,
  getClassSessionDurationInMs,
  isStudentPresent,
} from "utils/metrics-utils";
import { generateSessionLogCSV } from "utils/reporting";

type SortField =
  | "started_at"
  | "duration"
  | "class_type"
  | "attendance"
  | "hand_raises"
  | "reactions"
  | "chat_messages"
  | "student_talk_time"
  | "instructor_talk_time"
  | "your_attendance"
  | "your_time_in_class"
  | "recording"
  | "recording_setup";

const getSortValue = (session: ExtendedClassSessionSummaryType, field: SortField) => {
  const duration = getClassSessionDurationInMinutes(session);
  const isPresent = isStudentPresent(session.student_presence_minutes);
  switch (field) {
    case "started_at":
      return new Date(session.started_at).getTime();
    case "duration":
      return duration;
    case "class_type":
      return session.category === ClassSessionCategory.VIRTUAL ? 1 : 0;
    case "attendance":
      return session.attendance_percentage;
    case "hand_raises":
      return session.hand_raise_count ?? 0;
    case "reactions":
      return session.reaction_count ?? 0;
    case "chat_messages":
      return session.chat_message_count ?? 0;
    case "student_talk_time":
      return session.student_talk_time_seconds ?? 0;
    case "instructor_talk_time":
      return session.instructor_talk_time_seconds ?? 0;
    case "your_attendance":
      return isPresent ? 1 : 0;
    case "your_time_in_class":
      return session.student_presence_minutes ?? 0;
    case "recording":
    case "recording_setup":
      return 0; // Non-sortable fields
    default:
      return 0;
  }
};

function isRecordingAvailable(recordingSession: RecordingSessionType) {
  // We have no deterministic way to know when a recording is finished
  // being processed, so the following calculation makes a guess.
  // Processing of a standard 100 minute recording (90 minutes of class
  // time plus a 10 minute overrun) typically takes 30-35 minutes, but
  // sometimes as long as 40 minutes, so we can make a general assumption
  // processing time might be as much as 40% of recording time.
  if (
    !recordingSession.started_at ||
    !recordingSession.stopped_at ||
    recordingSession.state !== RecordingSessionState.STOPPED
  )
    return false;
  const startTime = new Date(recordingSession.started_at);
  const endTime = new Date(recordingSession.stopped_at);
  const processingTime = 0.4 * differenceInMinutes(endTime, startTime);
  const availableTime = addMinutes(endTime, processingTime);

  return availableTime && isPast(availableTime);
}

async function openRecordingInNewTab(recordingSessionId: string, setup: boolean = false) {
  const url = await serverGenerateRecordingUrl(recordingSessionId, setup);
  window.open(url, "_blank");
}

const formatAttendance = (classSession: ExtendedClassSessionSummaryType) => {
  if (classSession.students.length === 0) return "-";
  const percentage = Math.round(classSession.attendance_percentage);
  const percentageText = t("common.percentage", { number: percentage });

  return `${classSession.attending_students.length} / ${classSession.students.length} (${percentageText})`;
};

const formatTalkTime = (
  classSession: ExtendedClassSessionSummaryType,
  field: "student_talk_time_seconds" | "instructor_talk_time_seconds",
) => {
  if (classSession.category !== ClassSessionCategory.VIRTUAL) {
    return "-";
  }
  const seconds = classSession[field as keyof ExtendedClassSessionSummaryType] as
    | number
    | undefined;
  if (!seconds) return "-";

  const minutes = Math.floor(seconds / 60);
  const timeText = t("common.in_minutes", { minutes });

  const durationMs = getClassSessionDurationInMs(classSession);
  const percentage = durationMs > 0 ? Math.round(((seconds * 1000) / durationMs) * 100) : 0;
  const percentageText = t("common.percentage", { number: percentage });

  return `${timeText} (${percentageText})`;
};

const formatStudentAttendance = (classSession: ExtendedClassSessionSummaryType) => {
  const isPresent = isStudentPresent(classSession.student_presence_minutes);
  return isPresent ? <span>Present</span> : <span className="text-red">Absent</span>;
};

const formatStudentTimeInClass = (classSession: ExtendedClassSessionSummaryType) => {
  const presenceMinutes = classSession.student_presence_minutes;
  if (presenceMinutes === null || presenceMinutes === undefined) {
    return "-";
  }
  return t("common.in_minutes", { minutes: presenceMinutes });
};

interface ClassSessionRowProps {
  classSession: ExtendedClassSessionSummaryType;
  hasVirtualSessions: boolean;
}

const ClassSessionRow: FC<ClassSessionRowProps> = ({ classSession, hasVirtualSessions }) => {
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const { canAuthorSection, canParticipateInSection } = useCapabilities();

  const recordingSessions = useAppSelector((state) =>
    selectRecordingSessionsByClassSessionId(state, classSession.class_session_id),
  );

  const displayableRecordingSessions = recordingSessions
    .filter((rs) => !!rs.started_at)
    .sort((a, b) => a.started_at!.localeCompare(b.started_at!));

  const durationString = useMemo(() => {
    const duration = getClassSessionDurationInMinutes(classSession);
    if (!duration) return "-";
    return t("common.in_minutes", { minutes: duration });
  }, [classSession]);

  const handleHideSession = useCallback(async () => {
    try {
      await storeApi.class_sessions.partial_update({
        id: classSession.class_session_id,
        is_hidden_from_session_logs: true,
      });
    } catch {
      toastErrorMessage(t("session_log_view.hide_session.failed"));
    }
  }, [classSession.class_session_id]);

  const menuButton = (
    <SquareIconButton
      eventName="Session Log - Options menu"
      kind="ghost"
      size="s"
      title={t("common.options")}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <DotMenuVerticalIcon />
    </SquareIconButton>
  );

  return (
    <tr>
      <td>{format(classSession.started_at, "MMM dd yyyy, HH:mm")}</td>
      <td>{durationString}</td>
      <td>
        {classSession.category === ClassSessionCategory.VIRTUAL
          ? t("session_log_view.class_types.virtual-class")
          : t("session_log_view.class_types.in-person-class")}
      </td>
      {canAuthorSection && (
        <>
          <td>{formatAttendance(classSession)}</td>
          <td>{classSession.reaction_count ?? 0}</td>
          <td>{classSession.hand_raise_count ?? 0}</td>
          <td>{classSession.chat_message_count ?? 0}</td>
          {hasVirtualSessions && (
            <>
              <td>{formatTalkTime(classSession, "student_talk_time_seconds")}</td>
              <td>{formatTalkTime(classSession, "instructor_talk_time_seconds")}</td>
            </>
          )}
        </>
      )}
      {canParticipateInSection && (
        <>
          <td>{formatStudentAttendance(classSession)}</td>
          <td>{formatStudentTimeInClass(classSession)}</td>
        </>
      )}
      {hasVirtualSessions && (
        <td>
          {displayableRecordingSessions.length > 0
            ? displayableRecordingSessions.map((recordingSession, i) =>
                isRecordingAvailable(recordingSession) ? (
                  <LinkButton
                    key={recordingSession.id}
                    size="xs"
                    onClick={() => openRecordingInNewTab(recordingSession.id)}
                  >
                    {t("session_log_view.watch_recording")}
                    {displayableRecordingSessions.length > 1 ? ` ${i + 1}` : ""}
                  </LinkButton>
                ) : (
                  <p key={recordingSession.id}>{t("session_log_view.not_available")}</p>
                ),
              )
            : "-"}
        </td>
      )}
      {hasVirtualSessions && isMPAdmin && (
        <td>
          {displayableRecordingSessions.length > 0
            ? displayableRecordingSessions.map((recordingSession, i) => (
                <LinkButton
                  key={recordingSession.id}
                  size="xs"
                  onClick={() => openRecordingInNewTab(recordingSession.id, true)}
                >
                  Watch Setup
                  {displayableRecordingSessions.length > 1 ? ` ${i + 1}` : ""}
                </LinkButton>
              ))
            : "-"}
        </td>
      )}
      {canAuthorSection && (
        <td className="!px-0">
          <div className="flex justify-center">
            <Menu direction="bottom" menuButton={menuButton} menuClassName="min-w-[180px]">
              <MenuItem onClick={handleHideSession}>
                <EyeNotVisibleIcon className="text-black-tint-40" />
                <span className="text-black-tint-20">
                  {t("session_log_view.hide_session.button")}
                </span>
              </MenuItem>
            </Menu>
          </div>
        </td>
      )}
    </tr>
  );
};

interface SessionLogTableProps {
  dateRangeValue: number;
}

export const SessionLogTable: FC<SessionLogTableProps> = ({ dateRangeValue }) => {
  const isMPAdmin = useAppSelector(selectCurrentUserIsMPAdmin);
  const { canAuthorSection, canParticipateInSection } = useCapabilities();
  const classSessions = useAppSelector((state) =>
    selectExtendedClassSessionSummariesForDateRange(state, dateRangeValue),
  );

  const hasVirtualSessions = useMemo(() => {
    return classSessions.some((session) => session.category === ClassSessionCategory.VIRTUAL);
  }, [classSessions]);

  const sectionTitle = useAppSelector(selectCurrentSectionTitle);

  let numColumns = 3; // Started At, Duration, Class Type
  if (canAuthorSection) {
    numColumns += 5; // Attendance, Hand Raises, Reactions, Chat Messages, Options menu
    if (hasVirtualSessions) {
      numColumns += 2; // Student Talk Time, Instructor Talk Time
    }
  }
  if (canParticipateInSection) {
    numColumns += 2; // Your Attendance, Your Time in Class
  }
  if (hasVirtualSessions) {
    numColumns += 1; // Class Recording column
    if (isMPAdmin) {
      numColumns += 1; // Class Recording Setup column
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <SortableTableWrapper<ExtendedClassSessionSummaryType, SortField>
        className="class-log-dashboard-table"
        data={classSessions}
        defaultSortDirection="desc"
        defaultSortField="started_at"
        getSortValue={getSortValue}
      >
        <div className="mb-4 flex w-full items-center justify-between">
          <h3 className="h3 m-0 text-black-tint-40">{t("session_log_view.title")}</h3>
          {canAuthorSection && (
            <div className="flex gap-2">
              <SortableTableExportButton<ExtendedClassSessionSummaryType>
                buttonText={t("session_log_view.export.button")}
                disabledMessage={t("session_log_view.export.no_data")}
                errorMessage={t("session_log_view.export.failed")}
                generateCSV={(sortedData) =>
                  generateSessionLogCSV(sortedData, sectionTitle || "Course", hasVirtualSessions)
                }
              />
              <AttendanceReportButton />
            </div>
          )}
        </div>

        <SortableTable>
          <thead>
            <tr>
              <SortableHeader
                field="started_at"
                label={t("session_log_view.columns.started_at.label")}
                tooltip={t("session_log_view.columns.started_at.tooltip")}
              />
              <SortableHeader
                field="duration"
                label={t("session_log_view.columns.duration.label")}
                tooltip={t("session_log_view.columns.duration.tooltip")}
              />
              <SortableHeader
                field="class_type"
                label={t("session_log_view.columns.class_type.label")}
                tooltip={t("session_log_view.columns.class_type.tooltip")}
              />
              {canAuthorSection && (
                <>
                  <SortableHeader
                    field="attendance"
                    label={t("session_log_view.columns.attendance.label")}
                    tooltip={t("session_log_view.columns.attendance.tooltip")}
                  />
                  <SortableHeader
                    field="reactions"
                    label={t("session_log_view.columns.reactions.label")}
                    tooltip={t("session_log_view.columns.reactions.tooltip")}
                  />
                  <SortableHeader
                    field="hand_raises"
                    label={t("session_log_view.columns.hand_raises.label")}
                    tooltip={t("session_log_view.columns.hand_raises.tooltip")}
                  />
                  <SortableHeader
                    field="chat_messages"
                    label={t("session_log_view.columns.chat_messages.label")}
                    tooltip={t("session_log_view.columns.chat_messages.tooltip")}
                  />
                  {hasVirtualSessions && (
                    <>
                      <SortableHeader
                        field="student_talk_time"
                        label={t("session_log_view.columns.student_talk_time.label")}
                        tooltip={t("session_log_view.columns.student_talk_time.tooltip")}
                      />
                      <SortableHeader
                        field="instructor_talk_time"
                        label={t("session_log_view.columns.instructor_talk_time.label")}
                        tooltip={t("session_log_view.columns.instructor_talk_time.tooltip")}
                      />
                    </>
                  )}
                </>
              )}
              {canParticipateInSection && (
                <>
                  <SortableHeader
                    field="your_attendance"
                    label={t("session_log_view.columns.your_attendance.label")}
                    tooltip={t("session_log_view.columns.your_attendance.tooltip")}
                  />
                  <SortableHeader
                    field="your_time_in_class"
                    label={t("session_log_view.columns.your_time_in_class.label")}
                    tooltip={t("session_log_view.columns.your_time_in_class.tooltip")}
                  />
                </>
              )}
              {hasVirtualSessions && (
                <SortableHeader
                  field="recording"
                  label={t("session_log_view.columns.recording.label")}
                  sortable={false}
                  tooltip={t("session_log_view.columns.recording.tooltip")}
                />
              )}
              {hasVirtualSessions && isMPAdmin && (
                <SortableHeader
                  field="recording_setup"
                  label={t("session_log_view.columns.recording_setup.label")}
                  sortable={false}
                  tooltip={t("session_log_view.columns.recording_setup.tooltip")}
                />
              )}
              {canAuthorSection && <th aria-label="Options" className="w-4" />}
            </tr>
          </thead>

          <SortableBody<ExtendedClassSessionSummaryType>
            emptyColSpan={numColumns}
            emptyMessage={t("session_log_view.no_data")}
          >
            {(sortedData) =>
              sortedData.map((classSession) => (
                <ClassSessionRow
                  classSession={classSession}
                  hasVirtualSessions={hasVirtualSessions}
                  key={classSession.class_session_id}
                />
              ))
            }
          </SortableBody>
        </SortableTable>
      </SortableTableWrapper>
    </div>
  );
};
